import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// Für Konfetti:
import Confetti from 'react-confetti';

export default function CheckPage({
    frontImage,
    backImage,
    onNext,
    onRestart,
}) {
    const [loading, setLoading] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [apiError, setApiError] = useState(null);

    // Suchfunktion
    const [searchTerm, setSearchTerm] = useState('');

    // Konfetti-State
    const [showConfetti, setShowConfetti] = useState(false);

    const handleCheck = async () => {
        setLoading(true);
        setAnalysisResult(null);
        setApiError(null);

        try {
            const payload = {
                front_image_b64: frontImage,
                back_image_b64: backImage,
            };
            // Beispiel-Endpunkt
            const response = await axios.post('http://localhost:8000/api/analyse', payload, {
                headers: { 'Content-Type': 'application/json' },
            });
            setAnalysisResult(response.data);
        } catch (err) {
            if (err.response) {
                setApiError(`HTTP ${err.response.status} - ${err.response.statusText}`);
            } else {
                setApiError(err.message || 'Unbekannter Fehler');
            }
        } finally {
            setLoading(false);
        }
    };

    // notFulfilled
    const notFulfilled = analysisResult?.notFulfilled || [];

    // Geladene Daten
    const front = analysisResult?.frontSideData || {};
    const verordnet = analysisResult?.verordneterRezeptwert || {};
    const geleistet = analysisResult?.geleisteterRezeptwert || {};

    // Filter-Funktion
    function matchesSearch(value, label = '') {
        if (!searchTerm) return true;
        const txt = String(value || '') + ' ' + label;
        return txt.toLowerCase().includes(searchTerm.toLowerCase());
    }

    // Bool-zu-Icon
    function boolIcon(val) {
        return val ? <CheckIcon color="success" /> : <CloseIcon color="error" />;
    }

    // Klick auf "Alles korrigiert & weiter" => Konfetti für X Sek, dann onNext
    const handleAllesKorrigiert = () => {
        setShowConfetti(true);
        // Nach 3s -> onNext
        setTimeout(() => {
            setShowConfetti(false);
            onNext();
        }, 3000);
    };

    return (
        <Box sx={{ p: 2 }}>
            {showConfetti && <Confetti />}

            <Typography variant="h5" gutterBottom>
                Schritt 3: Verordnung prüfen
            </Typography>

            {!analysisResult && !loading && (
                <Button variant="contained" onClick={handleCheck}>
                    Jetzt Verordnung prüfen
                </Button>
            )}

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Analyse läuft...</Typography>
                </Box>
            )}

            {apiError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {apiError}
                </Alert>
            )}

            {analysisResult && (
                <Box sx={{ mt: 3 }}>

                    {/* notFulfilled => Alerts */}
                    {notFulfilled.map((nf, i) => (
                        <Alert key={i} severity="error" sx={{ mb: 1 }}>
                            <strong>{nf.bezeichnung}:</strong> {nf.erklaerung}
                            {nf.korrektur && (
                                <div>
                                    <em>Vorschlag:</em> {nf.korrektur}
                                </div>
                            )}
                        </Alert>
                    ))}

                    {/* Suchfeld */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="Suche in allen Tabellen"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Box>

                    {/* 0) Umsatz-Tabelle */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Tabelle: Umsatz mit diesem Rezept
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Umsatz Art</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Möglicher Umsatz</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Realisierter Umsatz</Box></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* 1: Vergütung */}
                                    {matchesSearch(verordnet.verguetung, 'verguetung') ||
                                        matchesSearch(geleistet.verguetung, 'verguetung') ? (
                                        <TableRow>
                                            <TableCell><Box sx={{ fontWeight: 'bold' }}>Vergütung</Box></TableCell>
                                            <TableCell>{verordnet.verguetung || ''}</TableCell>
                                            <TableCell>{geleistet.verguetung || ''}</TableCell>
                                        </TableRow>
                                    ) : null}
                                    {/* 2: Zuzahlung */}
                                    {matchesSearch(verordnet.zuzahlung, 'zuzahlung') ||
                                        matchesSearch(geleistet.zuzahlung, 'zuzahlung') ? (
                                        <TableRow>
                                            <TableCell><Box sx={{ fontWeight: 'bold' }}>Zuzahlung</Box></TableCell>
                                            <TableCell>{verordnet.zuzahlung || ''}</TableCell>
                                            <TableCell>{geleistet.zuzahlung || ''}</TableCell>
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* 1) Patientenstammdaten */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Tabelle: Patientenstammdaten
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Verordnungsfeld</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Wert in der Verordnung</Box></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderRow('Krankenkasse', front.krankenkasse)}
                                    {renderRow('NameVorname', front.nameVorname)}
                                    {renderRow('Geburtsdatum', front.geburtsdatum)}
                                    {renderRow('StrasseHausnummer', front.strasseHausnummer)}
                                    {renderRow('Postleitzahl', front.postleitzahl)}
                                    {renderRow('Ort', front.ort)}
                                    {renderRow('Kostentraegerkennung', front.kostentraegerkennung)}
                                    {renderRow('versichertennummer', front.versichertennummer)}
                                    {renderRow('status', front.status)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* 2) Rezeptdaten */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Tabelle: Rezeptdaten
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Verordnungsfeld</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>Wert in der Verordnung</Box></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderRow('Ausstellungsdatum (datum)', front.datum)}
                                    {renderRow('ICD-10', front.icd10Code1)}
                                    {renderRow('Diagnose(n)-Text', front['behandlungsrelevante Diagnose(n)-Text'])}
                                    {renderRow('ICD-10 (2)', front.icd10Code2)}
                                    {renderRow('Diagnose(n)-Text (2)', front.diagnoseText2)}
                                    {renderRow('diagnoseGruppe', front.diagnoseGruppe)}
                                    {renderRow('leitsymptomatikKatalog', front.leitsymptomatikKatalog)}
                                    {renderRow('leitsymptomatikIndividuell', front.leitsymptomatikIndividuell)}
                                    {renderRow('leitsymptomatikFreitext', front.leitsymptomatikFreitext)}
                                    {renderBoolRow('therapiebericht', front.therapiebericht)}
                                    {renderBoolRow('hausbesuch', front.hausbesuch)}
                                    {renderRow('therapiefrequenz', front.therapiefrequenz)}
                                    {renderBoolRow('dringlicherBehandlungsbedarf', front.dringlicherBehandlungsbedarf)}
                                    {renderRow('ikLeistungserbringer', front.ikLeistungserbringer)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* 3) Heilmittel */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Tabelle: Heilmittel
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>heilmittel bezeichnung</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>behandlungseinheiten</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>positionsnummer</Box></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderHeilRow(front.heilmittel1, front.behandlungseinheiten1, front.heilmittel1_posNr)}
                                    {renderHeilRow(front.heilmittel2, front.behandlungseinheiten2, front.heilmittel2_posNr)}
                                    {renderHeilRow(front.heilmittel3, front.behandlungseinheiten3, front.heilmittel3_posNr)}
                                    {renderHeilRow(front.ergaenzendesHeilmittel1, front.ergaenzendesHeilmittelEinheiten1, front.ergaenzendesHeilmittel1_posNr)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* 4) Erbrachte Leistungen */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Tabelle: Erbrachte Leistungen
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>datum</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>massnahmen</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>unterschriftLeistungserbringer</Box></TableCell>
                                        <TableCell><Box sx={{ fontWeight: 'bold' }}>unterschriftVersicherter</Box></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analysisResult?.backSideData?.behandlungen?.map((b, idx) => {
                                        const rowText = JSON.stringify(b).toLowerCase();
                                        if (searchTerm && !rowText.includes(searchTerm.toLowerCase())) {
                                            return null;
                                        }
                                        return (
                                            <TableRow key={idx}>
                                                <TableCell>{b.datum || ''}</TableCell>
                                                <TableCell>{b.massnahmen || ''}</TableCell>
                                                <TableCell>{boolIcon(b.unterschriftLeistungserbringer)}</TableCell>
                                                <TableCell>{boolIcon(b.unterschriftVersicherter)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Buttons unten */}
                    <Box sx={{ textAlign: 'right', mt: 2 }}>
                        {notFulfilled.length > 0 ? (
                            // Nur "Vorgang erneut starten"
                            <Button variant="contained" color="error" onClick={onRestart}>
                                Vorgang erneut starten
                            </Button>
                        ) : (
                            // Keine Fehler => 2 Buttons: "Weiter" und "Alles korrigiert & weiter" (Konfetti!)
                            <>
                                <Button variant="outlined" color="primary" onClick={onNext} sx={{ mr: 2 }}>
                                    Weiter
                                </Button>

                                <Button variant="contained" color="success" onClick={handleAllesKorrigiert}>
                                    Alles korrigiert & weiter
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );

    // ============ Hilfsfunktionen ===========

    function renderRow(label, value) {
        const isVisible = matchesSearch(value, label);
        if (!isVisible) return null;
        return (
            <TableRow>
                <TableCell>
                    <Box sx={{ fontWeight: 'bold' }}>{label}</Box>
                </TableCell>
                <TableCell>{value || ''}</TableCell>
            </TableRow>
        );
    }

    function renderBoolRow(label, boolVal) {
        const isVisible = matchesSearch(String(boolVal), label);
        if (!isVisible) return null;
        return (
            <TableRow>
                <TableCell>
                    <Box sx={{ fontWeight: 'bold' }}>{label}</Box>
                </TableCell>
                <TableCell>{boolIcon(boolVal)}</TableCell>
            </TableRow>
        );
    }

    function renderHeilRow(heil, einheiten, posnr) {
        if (!heil && !einheiten && !posnr) return null;
        const rowText = [heil, einheiten, posnr].join(' ').toLowerCase();
        if (searchTerm && !rowText.includes(searchTerm.toLowerCase())) return null;

        return (
            <TableRow>
                <TableCell>{heil || ''}</TableCell>
                <TableCell>{einheiten || ''}</TableCell>
                <TableCell>{posnr || ''}</TableCell>
            </TableRow>
        );
    }


    function boolIcon(val) {
        return val ? <CheckIcon color="success" /> : <CloseIcon color="error" />;
    }
}
